import { definePermissions } from '@eis/core';

/**
 * Billing permission catalog (Addendum A.2 "ownership"; SDD-015 §7.4, B-15).
 *
 * This file is the ONLY place in the module where permission literals may appear — ESLint
 * enforces that. Everything else references `billingPermissions.invoices.view`, so a typo is a
 * compile error rather than a permission that silently denies in production.
 *
 * The same five keys are enforced SERVER-SIDE per RPC by the billing service's authz interceptor
 * (DECISIONS A-23; `grpcapi.RequiredPermission`) — the console's gates are UX, the service is the
 * boundary, and the two spell one catalog. `permissions-registry.json` is generated from here and
 * diffed against the backend's list.
 */
export const billingPermissions = definePermissions({
  /**
   * `view` — invoices, bills, the schedule (`policies-billings`), the estimate; the module itself.
   *
   * `create` — issuing an invoice now (initial / recurring), a manual invoice with free items, an
   * estimate on demand, recording a status and creating or pausing the schedule row. All of these
   * bring a NEW document or a new schedule state into being for money that was not yet invoiced.
   *
   * `correct` is separate and deliberately so, as `policies.correct` is in the insurance module: a
   * correction, a reversal, a final (termination) or a withdraw invoice RE-STATE money that has
   * already been invoiced (SDD-015 §6.3/§6.4, B-11). Who may re-state invoiced history is a
   * different question from who may issue the next invoice, and modelling it as `create` would
   * answer it by accident in the direction that grants more.
   */
  invoices: {
    domain: 'billing-management',
    entity: 'invoices',
    actions: ['view', 'create', 'correct'],
  },
  /**
   * A bill run is its OWN entity, not an action on invoices: one run issues, estimates or
   * reconciles the tenant's whole book for a date (SDD-015 §5.6). Whoever may issue one invoice
   * is not thereby allowed to run every policy's billing — and reading a run's report (`view`)
   * is the operations persona's, while starting one (`run`) is the finance persona's and the
   * admin's.
   */
  billRuns: {
    domain: 'billing-management',
    entity: 'bill-runs',
    actions: ['view', 'run'],
  },
  /**
   * An EXPORT of billing data (SDD-020 J-20, §10, prompt 28): its own entity, as in
   * policy-management — taking invoices out as a file is not the same right as reading them.
   * finance-clerk holds it through `billing-management.*`; the service also checks the view key.
   */
  exports: {
    domain: 'billing-management',
    entity: 'exports',
    actions: ['create'],
  },
});
